import { NavHeader } from '@/components/features/shared/NavHeader'
import { SummaryExtended } from '@/components/features/shared/SummaryExtended'
import { PriceLabelSimple } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Offering, OfferingStatus, OfferingType, formatOfferingStatus } from '@/domain/offerings/offering'
import { useOfferings } from '@/hooks/api/useOfferings'
import { Paths } from '@/routes/paths'
import { useNavigate } from '@tanstack/react-router'
import { CircleCheck, Edit, FileText, Package, Plus, Repeat } from 'lucide-react'
import { useState } from 'react'
import { LoaderElement } from '../shared/LoaderElement'
import { getOfferingTypeIcon, serviceTypeBadge } from './common'

export const Offerings = () => {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  const { data: offerings, isLoading } = useOfferings({
    search,
    limit: 1000,
    offset: 0,
  })

  const handleCreate = () => {
    navigate({
      to: `${Paths.Management.NewOffering}`,
    })
  }

  const handleEdit = (offeringId: string) => {
    navigate({
      to: `${Paths.Management.Offerings}/${offeringId}`,
    })
  }

  if (isLoading) {
    return <LoaderElement />
  }
  const activeOfferings = offerings?.filter((o: Offering) => o.status === OfferingStatus.ACTIVE).length || 0
  const totalOfferings = offerings?.length || 0

  return (
    <div className="space-y-6">
      <NavHeader hideBackButton
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Servicio
          </Button>
        }
      >
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Servicios</h1>
          <p className="text-muted-foreground">
            Gestiona los servicios ofrecidos a los clientes
          </p>
        </div>
      </NavHeader>

      <SummaryExtended
        items={[
          {
            title: 'Servicios Activos',
            value: activeOfferings,
            icon: <CircleCheck className="h-4 w-4 text-emerald-600" />,
            subtitle: totalOfferings > 0 ? `${((activeOfferings / totalOfferings) * 100).toFixed(0)}% del total` : 'Sin servicios',
            subtitleClassName: 'text-xs text-muted-foreground mt-1',
            valueClassName: 'text-2xl font-bold',
          },
          {
            title: 'Servicios de Única Vez',
            value: offerings?.filter((o: Offering) => o.offeringType === OfferingType.ONE_TIME).length || 0,
            icon: <FileText className="h-4 w-4 text-muted-foreground" />,
            valueClassName: 'text-2xl font-bold',
          },
          {
            title: 'Servicios Recurrentes',
            value: offerings?.filter((o: Offering) => o.offeringType === OfferingType.RECURRING).length || 0,
            icon: <Repeat className="h-4 w-4 text-muted-foreground" />,
            valueClassName: 'text-xl font-bold',
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>Catálogo de Servicios</CardTitle>
          <CardDescription>
            Lista completa de servicios disponibles
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!offerings || offerings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="mx-auto h-12 w-12 mb-4" />
              <p>No hay servicios disponibles</p>
            </div>
          ) : (
            <div className="space-y-4">
              {offerings.map((off: Offering) => {
                return (
                  <div key={off.id}
                    className={`flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors
                  ${(off.status !== OfferingStatus.ACTIVE ? 'bg-[#e5e5e5]' : '')}`}>
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-muted rounded-lg">
                        {getOfferingTypeIcon(off.offeringType)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {serviceTypeBadge(off.serviceType)}
                          <p className="font-medium">{off.name}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm font-mono">
                            Código: <span className="font-mono">{off.code}</span>
                          </span>
                          <PriceLabelSimple price={off.basePrice} taxRate={off.taxRate} />
                          {off.isAdditional && (
                            <span className="ml-2 text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Servicio adicional</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-5">
                      <Badge
                        variant="secondary"
                        className={`hidden sm:block font-bold ${off.status === OfferingStatus.ACTIVE ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                            off.status === OfferingStatus.INACTIVE ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                              'bg-red-100 text-red-700 hover:bg-red-100'
                          }`}
                      >
                        {formatOfferingStatus(off.status)}
                      </Badge>
                      <IconButton
                        variant="warning"
                        onClick={() => handleEdit(off.id)}>
                        <Edit className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
